/**
 * «Skriv ut» on a banner (MKK #34 «Utskrifter», owner 2026-10-29): the banner as a poster, A4 or A3 portrait.
 *
 * The same drawing as «Del» (lib/social.ts, core social-share.layout) on a print-sized canvas (core
 * printables.POSTER_SIZES, 200 dpi), as a PNG to download — or straight to the printer from a small page that holds only
 * the picture, sized to the paper. Drawn in the browser; nothing is sent anywhere, nothing is stored.
 */
import React, { useEffect, useState } from 'react';
import { saveBlob } from '../lib/api';
import { drawPoster, mosquePublic, type ShareSource } from '../lib/social';
import { Btn } from './ui';
import { useToastOn } from '../lib/hooks';
import { Printables } from '../core';

/** The page the printer gets: the picture alone, filling the paper. `url` is a blob: URL made here. */
export function printPage(url: string, sizeId: string, title: string): string {
  const z = Printables.POSTER_SIZES[sizeId] || Printables.POSTER_SIZES.a4;
  const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  return `<!doctype html><html lang="no"><head><meta charset="utf-8"><title>${esc(title)}</title>`
    + `<style>@page{size:${z.mm[0]}mm ${z.mm[1]}mm;margin:0}html,body{margin:0;padding:0;background:#fff}`
    + `img{display:block;width:${z.mm[0]}mm;height:${z.mm[1]}mm;object-fit:contain}</style></head>`
    + `<body><img src="${esc(url)}" alt="${esc(title)}"></body></html>`;
}

export function PosterPanel({ source, title, onClose }: { source: ShareSource; title: string; onClose: () => void }) {
  const [size, setSize] = useState(Printables.POSTER_ORDER[0]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useToastOn(msg, 'err');
  const [short, setShort] = useState('');
  useEffect(() => { void mosquePublic().then((m) => setShort(m.shortName)); }, []);

  async function make(): Promise<Blob | null> {
    setBusy(true); setMsg('');
    const blob = await drawPoster(source, size, short);
    setBusy(false);
    if (!blob) setMsg('Plakaten kunne ikke lages. Prøv igjen, eller last ned bildet fra «Del».');
    return blob;
  }
  async function downloadPng() {
    const blob = await make();
    if (blob) saveBlob(blob, Printables.posterFileName(title, size));
  }
  async function print() {
    // The window is opened on the click itself (a window opened after an await is blocked as a popup), then filled.
    const w = window.open('', '_blank');
    if (!w) { setMsg('Nettleseren stoppet utskriftsvinduet. Tillat sprettoppvinduer for panelet, eller last ned PNG og skriv ut den.'); return; }
    const blob = await make();
    if (!blob) { w.close(); return; }
    const url = URL.createObjectURL(blob);
    w.document.open(); w.document.write(printPage(url, size, title)); w.document.close();
    const img = w.document.querySelector('img');
    const go = () => { w.focus(); w.print(); setTimeout(() => URL.revokeObjectURL(url), 60000); };
    if (img && !img.complete) img.addEventListener('load', go, { once: true }); else go();
  }

  return (
    <div className="share poster" role="region" aria-label={`Skriv ut «${title}» som plakat`}>
      <div className="row" style={{ alignItems: 'center' }}>
        <span className="sub">Plakat:</span>
        {Printables.POSTER_ORDER.map((id) => (
          <label key={id} className="chk"><input type="radio" name="poster-size" checked={size === id} onChange={() => setSize(id)} /> {Printables.POSTER_SIZES[id].label_no}</label>
        ))}
        <div className="sp" style={{ flex: 1 }} />
        <Btn disabled={busy} onClick={() => void downloadPng()}>{busy ? 'Lager …' : 'Last ned PNG'}</Btn>
        <Btn ghost disabled={busy} onClick={() => void print()}>Skriv ut</Btn>
        <Btn ghost onClick={onClose}>Lukk</Btn>
      </div>
    </div>
  );
}
